import { useMemo } from 'react'
import styled from 'styled-components'
import { skip, take, sortBy } from 'lib/array/arrayUtils'
import useToggle from 'hooks/useToggle'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Expander from 'components/utils/Expander'
import ExperienceTicketItem from './ExperienceTicketItem'
import Heading from 'LuxKit/Typography/Heading'
import { FormattedMessage, useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'

const TicketOptions = styled.div`
  > *:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const AdditionalTicketOptions = styled(TicketOptions)`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const CenteredToggle = styled(SmallToggleButton)`
  display: flex;
  margin: 0 auto;
`

const RoundedInfoBanner = styled(MessageBanner)`
  border-radius: ${props => props.theme.borderRadius.S};
`

const DescriptionLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  color: inherit;
  font: inherit;
  font-weight: bold;
`

interface Props {
  onTicketCountChange?: (ticket: App.ExperienceBookingTicketView, nextCount: number) => void
  ticketCounts: any
  ticketOptions: Array<App.ExperienceBookingTicketView>
  purchaseLimitRemaining?: number
  purchaseLimitReason?: App.ExperiencePurchaseLimit['maxReason']
  orderMode?: App.ExperienceOrderMode
  bookingType?: App.ExperienceOffer['bookingType']
  onLimitedAvailabilityBannerClick?: () => void
  className?: string
  afterHeading?: React.ReactNode
  performanceWarning?: string
  hideAvailabilityBanner?: boolean
  offer: App.ExperienceOffer
}

const initialMaxVisibleOptions = 4

function ExperienceTicketSelection(props: Props) {
  const {
    ticketOptions,
    ticketCounts,
    onTicketCountChange,
    className,
    purchaseLimitRemaining,
    purchaseLimitReason,
    orderMode = 'booking',
    bookingType,
    onLimitedAvailabilityBannerClick,
    afterHeading,
    performanceWarning,
    hideAvailabilityBanner,
    offer,
  } = props
  const [showAll, toggleShowAll] = useToggle()
  const intl = useIntl()

  const sortedTickets = useMemo(() => sortBy(ticketOptions, ticket => !!ticket.isExtra, 'asc'), [ticketOptions])

  return <div className={className}>
    {sortedTickets.length > 0 && <>
      <VerticalSpacer gap={16}>
        <Heading variant="heading6">{intl.formatMessage({
          id: '-nfbSFTi4dJZxPco0s_X',
          defaultMessage: 'Select an option',
          description: 'Section heading for experience ticket option selection',
        })}
        </Heading>
        {afterHeading}
        {performanceWarning && (
          <BodyText variant="small" colour="warning">{performanceWarning}</BodyText>
        )}
        {bookingType === 'NO-CALENDAR-FIXED-END' && !hideAvailabilityBanner && <>
          <RoundedInfoBanner
            kind="informational-primary"
            density="compact"
            icon={<LineInfoCircleIcon />}
          >
            <FormattedMessage
              id="WIpn29gvSAv4qFuuwrDM"
              defaultMessage="This experience is only available on selected days. Please <link>check the description</link> for details."
              description="Info banner shown above ticket options for voucher-based experiences with NO-CALENDAR-FIXED-END booking type, indicating the experience is not available every day. The 'link' placeholder wraps the clickable text that scrolls to the description section."
              values={{
                link: (chunks) => onLimitedAvailabilityBannerClick
                  ? <DescriptionLink type="button" onClick={onLimitedAvailabilityBannerClick}>{chunks}</DescriptionLink>
                  : <>{chunks}</>,
              }}
            />
          </RoundedInfoBanner>
          <Divider kind="primary" />
        </>}
      </VerticalSpacer>
      <TicketOptions>
        {take(sortedTickets, initialMaxVisibleOptions).map(ticket =>
          <ExperienceTicketItem
            key={ticket.id}
            ticket={ticket}
            count={ticketCounts[ticket.id]}
            onTicketCountChange={onTicketCountChange}
            purchaseLimitRemaining={purchaseLimitRemaining}
            purchaseLimitReason={purchaseLimitReason}
            orderMode={orderMode}
            offer={offer}
          />)}
      </TicketOptions>
      {sortedTickets.length > initialMaxVisibleOptions && <>
        <Expander expanded={showAll}>
          <AdditionalTicketOptions>
            {skip(sortedTickets, initialMaxVisibleOptions).map(ticket =>
              <ExperienceTicketItem
                key={ticket.id}
                ticket={ticket}
                count={ticketCounts[ticket.id]}
                onTicketCountChange={onTicketCountChange}
                purchaseLimitRemaining={purchaseLimitRemaining}
                purchaseLimitReason={purchaseLimitReason}
                orderMode={orderMode}
                offer={offer}
              />)}
          </AdditionalTicketOptions>
        </Expander>
        <CenteredToggle
          onClick={toggleShowAll}
          size="medium"
          on={showAll}
        >
          {!showAll && <>{intl.formatMessage(
            {
              id: 'hfwc5RVWzwBw9B1evwDY',
              defaultMessage: 'View {count, plural, one {# more option} other {# more options}}',
              description: 'Toggle button to show additional hidden ticket options',
            },
            { count: sortedTickets.length - initialMaxVisibleOptions },
          )}
          </>}
          {showAll && <>{intl.formatMessage({
            id: '3o4EHssVvlj4rSXDBubi',
            defaultMessage: 'View less options',
            description: 'Toggle button to collapse additional ticket options',
          })}
          </>}
        </CenteredToggle>
      </>}
    </>}
  </div>
}

export default ExperienceTicketSelection
